'use client';
import React, { useState } from 'react';
import { Star, CheckCircle, ChevronLeft, ChevronRight } from 'lucide-react';

interface Review {
  id: string;
  name: string;
  avatar: string;
  avatarAlt: string;
  rating: number;
  text: string;
  trip: string;
  date: string;
  verified: boolean;
}

// BACKEND INTEGRATION: Replace with GET /api/reviews?approved=true
const reviews: Review[] = [
{
  id: 'review-001',
  name: 'Priya Sharma',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1297ac811-1763295122319.png",
  avatarAlt: 'Indian woman smiling with dark hair and professional attire',
  rating: 5,
  text: 'Excellent service! Satish ji ka driver bahut professional tha. Innova Crysta mein Kedarnath trip ekdum smooth rahi. Highly recommended!',
  trip: 'Delhi → Kedarnath',
  date: '28 Apr 2026',
  verified: true
},
{
  id: 'review-002',
  name: 'Rahul Verma',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1b5ea51b0-1763293852695.png",
  avatarAlt: 'Young Indian man in casual shirt smiling outdoors',
  rating: 5,
  text: 'Booked Swift Dzire for airport drop. On time, clean car, courteous driver. OTP system is great for security. Will book again!',
  trip: 'Delhi Local — IGI Airport',
  date: '15 May 2026',
  verified: true
},
{
  id: 'review-003',
  name: 'Sunita Agarwal',
  avatar: "https://images.unsplash.com/photo-1624354865912-fdf2f0e09a21",
  avatarAlt: 'Middle aged Indian woman with bindi smiling warmly',
  rating: 5,
  text: 'Char Dham yatra ke liye best service. Driver Ramesh ji bahut helpful the. 12 din ki trip mein koi problem nahi aayi. Shukriya Navya Travels!',
  trip: 'Char Dham Yatra — 12 Days',
  date: '10 May 2026',
  verified: true
},
{
  id: 'review-004',
  name: 'Amit Bhatia',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_17d890d6b-1772316635807.png",
  avatarAlt: 'Indian man in blue shirt with glasses smiling at camera',
  rating: 4,
  text: 'Jaipur trip was wonderful. Tempo Traveller was comfortable for our group of 10. Pricing was fair and transparent. Good experience overall.',
  trip: 'Delhi → Jaipur — Group Tour',
  date: '02 May 2026',
  verified: true
},
{
  id: 'review-005',
  name: 'Kavita Mehta',
  avatar: "https://images.unsplash.com/photo-1727740443703-11eace1e0d9e",
  avatarAlt: 'Young Indian woman with kurta and earrings smiling brightly',
  rating: 5,
  text: 'Kashmir trip was a dream! Driver knew all the scenic routes. Ertiga was spacious for our family of 5. Navya Travels is our go-to for all trips now.',
  trip: 'Delhi → Kashmir — 7 Days',
  date: '25 Apr 2026',
  verified: true
},
{
  id: 'review-006',
  name: 'Deepak Joshi',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1b5ea51b0-1763293852695.png",
  avatarAlt: 'Indian man with stubble and confident smile in outdoor setting',
  rating: 5,
  text: 'Booked for Agra day trip. Hyundai Aura was clean and AC was excellent in summer heat. Driver was punctual and very knowledgeable about Agra.',
  trip: 'Delhi → Agra Day Trip',
  date: '08 May 2026',
  verified: true
}];


export default function ReviewsSection() {
  const [page, setPage] = useState(0);
  const perPage = 3;
  const totalPages = Math.ceil(reviews.length / perPage);
  const visible = reviews.slice(page * perPage, page * perPage + perPage);

  const avgRating = (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1);

  return (
    <section id="reviews" className="py-20 px-6 lg:px-10 section-bg">
      <div className="max-w-screen-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="text-xs font-bold text-primary uppercase tracking-widest mb-3 block">
            Customer Reviews
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-4">
            What Our <span className="gradient-blue-text">Customers Say</span>
          </h2>

          {/* Rating Summary */}
          <div className="inline-flex items-center gap-3 glass-card border border-primary/15 px-6 py-3 rounded-full">
            <div className="flex">
              {[1, 2, 3, 4, 5].map((s) =>
              <Star key={`star-avg-${s}`} size={16} className="text-amber-400 fill-amber-400" />
              )}
            </div>
            <span className="text-white font-bold text-lg">{avgRating}</span>
            <span className="text-muted-foreground text-sm">/ 5 from {reviews.length} verified reviews</span>
          </div>
        </div>

        {/* Reviews Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
          {visible.map((review) =>
          <div key={review.id} className="review-card p-5">
              {/* Top Row */}
              <div className="flex items-start gap-3 mb-3">
                <img
                src={review.avatar}
                alt={review.avatarAlt}
                className="w-11 h-11 rounded-full object-cover border-2 border-primary/20" />
              
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-white text-sm">{review.name}</span>
                    {review.verified &&
                  <span className="flex items-center gap-1 text-xs text-green-400 font-semibold">
                        <CheckCircle size={11} /> Verified
                      </span>
                  }
                  </div>
                  <p className="text-xs text-muted-foreground">{review.trip}</p>
                </div>
                <span className="text-xs text-muted-foreground shrink-0">{review.date}</span>
              </div>

              {/* Stars */}
              <div className="flex gap-0.5 mb-3">
                {[1, 2, 3, 4, 5].map((s) =>
              <Star
                key={`${review.id}-star-${s}`}
                size={13}
                className={s <= review.rating ? 'text-amber-400 fill-amber-400' : 'text-muted'} />

              )}
              </div>

              {/* Text */}
              <p className="text-sm text-slate-300 leading-relaxed">{review.text}</p>
            </div>
          )}
        </div>

        {/* Pagination */}
        <div className="flex justify-center items-center gap-4">
          <button
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            className="p-2 rounded-lg glass-card border border-border/30 text-muted-foreground hover:text-white disabled:opacity-30 transition-all"
            aria-label="Previous reviews">
            
            <ChevronLeft size={18} />
          </button>
          <div className="flex gap-2">
            {Array.from({ length: totalPages }, (_, i) =>
            <button
              key={`page-btn-${i}`}
              onClick={() => setPage(i)}
              className={`w-2 h-2 rounded-full transition-all ${
              page === i ? 'bg-primary w-6' : 'bg-muted'}`
              }
              aria-label={`Go to page ${i + 1}`} />

            )}
          </div>
          <button
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={page === totalPages - 1}
            className="p-2 rounded-lg glass-card border border-border/30 text-muted-foreground hover:text-white disabled:opacity-30 transition-all"
            aria-label="Next reviews">
            
            <ChevronRight size={18} />
          </button>
        </div>

        {/* Note */}
        <p className="text-center text-xs text-muted-foreground mt-6">
          Only verified customers who completed a booking can submit reviews.
        </p>
      </div>
    </section>);

}